import { useEffect } from "react";
import { DialogContent, DialogTitle, DialogFooter, DialogHeader } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle, Info, X, ChevronDown } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useFuelForm } from "../../hooks/useFuelForm";
import { FuelEntry, Option } from "@/types/fuel";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface FuelModalFormProps {
  onClose: () => void;
  onSave: () => void;
  formEntry: FuelEntry;
  onFormEntryChange: (entry: FuelEntry) => void;
  sourceOptions: Option[];
  editing: boolean;
  availableUnits?: string[];
}

export const FuelModalForm = ({
  onClose,
  onSave,
  formEntry,
  onFormEntryChange,
  sourceOptions,
  editing,
  availableUnits = [],
}: FuelModalFormProps) => {
  const { t } = useLanguage();
  const {
    dropdownRef,
    search,
    showDropdown,
    fuelSourceError,
    quantityError,
    touched,
    filteredOptions,
    availableUnits: hookUnits,
    isFormValid,
    handleSourceChange,
    handleSourceSelect,
    handleInputClick,
    handleSourceBlur,
    handleQuantityChange,
    handleQuantityBlur,
    handleUnitChange,
    setUnitsForFuelSource,
    handleSaveClick,
  } = useFuelForm({
    formEntry,
    sourceOptions,
    editing,
    onFormEntryChange,
    onSave,
  });

  // Set available units when component mounts or when availableUnits prop changes
  useEffect(() => {
    if (availableUnits.length > 0) {
      setUnitsForFuelSource(availableUnits);
    }
  }, [availableUnits, setUnitsForFuelSource]);

  // Add additional useEffect to handle initial form setup for editing
  useEffect(() => {
    if (editing && formEntry.fuelSource) {
      const units = availableUnits.length > 0 ? availableUnits : [formEntry.unit];
      setUnitsForFuelSource(units);
    }
  }, [editing, formEntry.fuelSource, formEntry.unit, availableUnits, setUnitsForFuelSource]);

  // Determine if we should show the unit selector
  const hasMultipleUnits = hookUnits.length > 1;

  return (
    <DialogContent className="sm:max-w-[600px]">
      <DialogHeader>
        <DialogTitle>
          {editing ? t("common.edit") : t("common.add")} {t("footprints.categories.fuels.title")}
        </DialogTitle>
      </DialogHeader>

      <div className="grid gap-4 py-4">
        {/* Fuel Source */}
        <div className="grid gap-2">
          <Label htmlFor="fuelSource" className="font-medium">
            {t("fuels.fuelSource") || "Tipo de Combustível"} *
          </Label>
          <div className="relative" ref={dropdownRef}>
            <div className="relative">
              <input
                id="fuelSource"
                type="text"
                value={search}
                onChange={handleSourceChange}
                onBlur={handleSourceBlur}
                onClick={handleInputClick}
                placeholder={t("fuels.selectSource") || "Selecione o tipo de combustível"}
                autoComplete="off"
                spellCheck="false"
                className={`w-full h-11 px-3 py-2 cursor-pointer pr-10 border rounded-md outline-none focus:outline-none ${fuelSourceError && touched.fuelSource ? "border-red-500" : "border-gray-200"
                  }`}
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                {search && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSourceSelect({ value: "", label: "" }, e);
                    }}
                    className="text-gray-400 hover:text-gray-600 p-1 rounded-full mr-1"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
                <ChevronDown
                  className={`h-4 w-4 text-gray-500 transition-transform ${showDropdown ? "transform rotate-180" : ""}`}
                />
              </div>
            </div>
            {showDropdown && filteredOptions.length > 0 && (
              <div
                className="absolute z-50 w-full mt-1 border bg-white rounded-md shadow-lg overflow-hidden"
                style={{ maxHeight: "320px", overflowY: "auto" }}
              >
                {filteredOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className="w-full text-left px-4 py-3.5 hover:bg-teal-50 cursor-pointer transition-colors border-b border-gray-100 last:border-0"
                    onMouseDown={(e) => handleSourceSelect(option, e)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {fuelSourceError && touched.fuelSource && (
            <div className="flex items-center text-xs text-red-500 mt-1">
              <AlertCircle className="h-3 w-3 mr-1" />
              {fuelSourceError}
            </div>
          )}
        </div>

        {/* Quantity with unit */}
        <div className="grid gap-2">
          <Label htmlFor="quantity" className="font-medium">
            {t("common.quantity")} *
          </Label>

          {!formEntry.fuelSource ? (
            <div className="space-y-4">
              <Input
                id="quantity"
                type="number"
                step="0.01"
                min="0"
                value={formEntry.quantity === "" ? "" : formEntry.quantity}
                onChange={(e) => handleQuantityChange(e.target.value)}
                onBlur={handleQuantityBlur}
                placeholder="0.00"
                disabled={!formEntry.fuelSource}
                className={`py-3 rounded-md text-lg ${quantityError
                    ? "border-red-500 focus:ring-red-500"
                    : "border-gray-200 focus:ring-teal-500 focus:border-teal-500"
                  }`}
              />

              <div className="bg-gray-50 border border-gray-200 rounded-md p-4 flex items-center gap-3 text-gray-600">
                <Info className="h-5 w-5 text-teal-600 flex-shrink-0" />
                <p className="text-sm">{t("fuels.selectFuelInfo") || "Selecione um tipo de combustível"}</p>
              </div>
            </div>
          ) : hasMultipleUnits ? (
            // When multiple units are available, show input with unit selector
            <div className="grid grid-cols-[1fr,auto] gap-2">
              <div>
                <Input
                  id="quantity"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formEntry.quantity === "" ? "" : formEntry.quantity}
                  onChange={(e) => handleQuantityChange(e.target.value)}
                  onBlur={handleQuantityBlur}
                  placeholder="0.00"
                  className={`py-3 rounded-md text-lg ${quantityError
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-200 focus:ring-teal-500 focus:border-teal-500"
                    }`}
                />
              </div>
              <Select value={formEntry.unit} onValueChange={handleUnitChange}>
                <SelectTrigger className="w-24">
                  <SelectValue placeholder={t("common.unit")} />
                </SelectTrigger>
                <SelectContent>
                  {hookUnits.map((unit) => (
                    <SelectItem key={unit} value={unit}>
                      {unit}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            // When only one unit is available, show input with unit displayed
            <div className="relative">
              <Input
                id="quantity"
                type="number"
                step="0.01"
                min="0"
                value={formEntry.quantity === "" ? "" : formEntry.quantity}
                onChange={(e) => handleQuantityChange(e.target.value)}
                onBlur={handleQuantityBlur}
                placeholder="0.00"
                className={`pr-16 py-3 rounded-md text-lg ${quantityError
                    ? "border-red-500 focus:ring-red-500"
                    : "border-gray-200 focus:ring-teal-500 focus:border-teal-500"
                  }`}
              />
              <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none text-gray-500">
                {formEntry.unit}
              </div>
            </div>
          )}

          {quantityError && touched.quantity && (
            <div className="flex items-center text-xs text-red-500 mt-1">
              <AlertCircle className="h-3 w-3 mr-1" />
              {t("validation.positiveNumber")}
            </div>
          )}
        </div>

        {/* Information Notes - Only show after user selects a fuel type */}
        {formEntry.fuelSource && (
          <div className="bg-blue-50 border border-blue-100 rounded-md p-4 mt-2">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-gray-700 space-y-2">
                <p>
                  {t("fuels.notesToShow") ||
                    "Nesta secção, deve reportar os valores anuais referentes aos combustíveis consumidos nas operações da empresa que são utilizados para a produção dos produtos."}
                </p>
                <p>
                  {t("fuels.transformationNote") ||
                    "Não incluir os consumos de combustível usados no transporte da pedra da pedreira para a transformação."}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t("common.cancel")}
        </Button>
        <Button
          type="button"
          onClick={() => handleSaveClick(t)}
          className="bg-teal-600 text-white hover:bg-teal-700"
          disabled={!isFormValid()}
        >
          {t("common.save")}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};
